<script lang="ts">
  /**
   * MobileDaysProjectCard — a compact card of the Days projects carousel: status
   * accent, category emoji + title, "Active · wk 4/6" and a progress bar. Tap opens
   * the project rail sheet. Task progress follows the shared task cache, so
   * completing a task in the rail moves the bar. Contract: plans/mobile-v2.md §3.3.
   */
  import { onMount, untrack } from 'svelte';
  import type { Project, ProjectStatus } from '../../lib/types';
  import { categoryIcons } from '../../lib/utils';
  import { focusAccent, focusColumn } from '../../lib/projectStatusActions';
  import { openProjectRail } from '../../lib/stores/ui';
  import { getCachedTasks, subscribeToCachedTasks } from '../../lib/stores/projectTasksCache';
  import { getTaskCounts } from '../../lib/useProjectTaskCounts';
  import { projectWeekPosition } from '../../lib/mobileDays';

  interface Props {
    project: Project;
    /** The week the carousel is showing, for "wk 4/6". */
    weekKey: string;
  }
  let { project, weekKey }: Props = $props();

  let cached = $state(untrack(() => getCachedTasks(project.id)));
  onMount(() =>
    subscribeToCachedTasks(project.id, (data) => {
      cached = data;
    }),
  );

  const STATUS_LABELS: Record<ProjectStatus, string> = {
    planned: 'Planned',
    active: 'Active',
    almost: 'Active',
    done: 'Done',
  };
  // "Almost" counts as Active, as in Focus.
  let column = $derived(focusColumn(project.status));
  let position = $derived(projectWeekPosition(project, weekKey));
  let statusText = $derived(
    position
      ? `${STATUS_LABELS[column]} · wk ${position.index}/${position.total}`
      : STATUS_LABELS[column],
  );
  let counts = $derived(
    cached.fetched
      ? {
          total: cached.tasks.length,
          done: cached.tasks.filter((t) => t.status !== 'todo').length,
        }
      : getTaskCounts(project),
  );
  let percent = $derived(counts.total ? Math.round((counts.done / counts.total) * 100) : 0);
</script>

<button
  type="button"
  class="card"
  style:--accent={focusAccent(project.status)}
  aria-label="{project.title}, {statusText}"
  onclick={() => openProjectRail(project.id)}
>
  <span class="title">
    <span aria-hidden="true"
      >{categoryIcons[project.category] || '📋'}{project.isPriority ? ' ★' : ''}</span
    >
    {project.title}
  </span>
  <span class="meta">
    <span>{statusText}</span>
    {#if counts.total}<span>{counts.done}/{counts.total}</span>{/if}
  </span>
  <span class="bar" aria-hidden="true"><i style:width="{percent}%"></i></span>
</button>

<style>
  .card {
    display: flex;
    flex: 0 0 172px;
    flex-direction: column;
    gap: 6px;
    min-height: 44px;
    padding: 10px 12px;
    text-align: left;
    font: inherit;
    color: #111827;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-left: 4px solid var(--accent);
    border-radius: 12px;
    cursor: pointer;
    scroll-snap-align: start;
    -webkit-tap-highlight-color: transparent;
  }

  .card:active {
    background: #f9fafb;
  }

  .title {
    overflow: hidden;
    font-size: 14.5px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
  }

  .bar {
    height: 4px;
    overflow: hidden;
    background: #e5e7eb;
    border-radius: 2px;
  }

  .bar i {
    display: block;
    height: 100%;
    background: var(--accent);
  }
</style>
